import { createRouter, createWebHistory } from 'vue-router'
import { authRoutes } from './auth-routes'
import bulkMessagingRoutes from './bulk-messaging-routes'
import homeRoutes from './home-routes'
import whatsappRoutes from './whatsapp-routes'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { useCampaignService } from '@/services/campaign/useCampaignService'

let campaignLoadPromise: Promise<void> | null = null

const ensureWorkspaceCampaigns = async () => {
  const workspace = useWorkspaceStore()
  if (workspace.hasLoadedCampaigns) return
  if (campaignLoadPromise) return campaignLoadPromise

  const campaignService = useCampaignService()
  workspace.setCampaignsLoading(true)
  campaignLoadPromise = campaignService
    .getAllCampaigns()
    .then((campaigns) => {
      workspace.setCampaigns(Array.isArray(campaigns) ? campaigns : [])
    })
    .finally(() => {
      workspace.setCampaignsLoading(false)
      campaignLoadPromise = null
    })

  return campaignLoadPromise
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...homeRoutes,
    authRoutes,
    ...bulkMessagingRoutes,
    ...whatsappRoutes,
    {
      path: '/checkout/result',
      name: 'checkout.result',
      component: () => import('@/pages/checkout/CheckoutResultPage.vue'),
      meta: { layout: 'DashboardLayout', requiresAuth: true },
    },

    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('oper_access_token')
  const userData = localStorage.getItem('oper_user_data')
  const isAuthenticated = !!(token && userData)

  if (to.meta.requiresAuth && !isAuthenticated) {
    if (to.fullPath !== '/auth/sign-in' && !to.path.startsWith('/whatsapp')) {
      localStorage.setItem('redirectAfterLogin', to.fullPath)
    }
    next('/auth/sign-in')
    return
  }

  if (isAuthenticated && to.path.startsWith('/auth')) {
    next('/')
    return
  }

  if (to.meta.requiresWhatsAppCampaign) {
    const workspace = useWorkspaceStore()

    try {
      await ensureWorkspaceCampaigns()
    } catch {
      next('/')
      return
    }

    if (!workspace.hasWhatsAppCampaign) {
      next('/')
      return
    }
  }

  if (to.name === 'home') {
    const workspace = useWorkspaceStore()
    const hasActiveCall = [
      'dialing',
      'ringing',
      'calling',
      'connected',
      'held',
    ].includes(workspace.webphoneCallStatus)
    const activeCampaignId = workspace.callCampaignId || workspace.selectedCampaignId

    if (hasActiveCall && activeCampaignId) {
      next({ name: 'campaign-detail', params: { id: activeCampaignId } })
      return
    }

    if (hasActiveCall && workspace.callReturnRoute) {
      next({ name: 'direct-call' })
      return
    }
  }

  next()
})

export default router